"use client";

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";

import { fetchData } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { useCommandSessions } from "@/hooks/use-command-sessions";
import type { CommandSession, CommandSessionList } from "@/types/command-agent";

function dedupeSessions(sessions: CommandSession[]): CommandSession[] {
    const seen = new Set<string>();
    return sessions.filter((session) => {
        if (seen.has(session.id)) return false;
        seen.add(session.id);
        return true;
    });
}

interface UseCommandV2AllSessionsOptions {
    agentId: string | null;
    projectIds: string[];
}

export function useCommandV2AllSessions({
    agentId,
    projectIds,
}: UseCommandV2AllSessionsOptions) {
    const { hasPermission: canListSessions } = useProtectedQuery({
        queryKey: ["_gated_list_command_sessions"],
        queryFn: async () => null,
        requiredPermissions: ["LIST_COMMAND_SESSIONS"],
        enabled: false,
    });

    const {
        allSessions: unfiledSessions,
        isLoadingSessions: isLoadingUnfiled,
        isFetchingSessions: isFetchingUnfiled,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        loadMoreRef,
    } = useCommandSessions(agentId, { projectId: null });

    const projectSessionQueries = useQueries({
        queries: projectIds.map((projectId) => ({
            queryKey: ["command-project-sessions-page", projectId],
            queryFn: async () => {
                const params = new URLSearchParams({
                    page: "1",
                    limit: "50",
                    sort_by: "updated_at",
                    sort_order: "desc",
                });
                return fetchData(
                    `/api/command-team/projects/${projectId}/sessions?${params.toString()}`
                ) as Promise<CommandSessionList>;
            },
            enabled: canListSessions && Boolean(projectId),
            staleTime: 30_000,
        })),
    });

    const allSessions = useMemo(() => {
        const projectSessions = projectSessionQueries.flatMap((query) => {
            const list = query.data as CommandSessionList | undefined;
            return list?.items ?? [];
        });
        return dedupeSessions([...unfiledSessions, ...projectSessions]);
    }, [unfiledSessions, projectSessionQueries]);

    const isLoadingProjectSessions = projectSessionQueries.some((query) => query.isLoading);
    const isFetchingProjectSessions = projectSessionQueries.some((query) => query.isFetching);

    return {
        allSessions,
        unfiledSessions,
        isLoadingUnfiled,
        isFetchingUnfiled,
        isLoadingSessions: isLoadingUnfiled || isLoadingProjectSessions,
        isFetchingSessions: isFetchingUnfiled || isFetchingProjectSessions,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        loadMoreRef,
    };
}
